import React from 'react';
import { Icon } from '@iwoca/orion';
import { t } from '@lingui/macro';
import { DisabledEarlyRepaymentCalculator } from 'components/EarlyRepaymentCalculator/DisabledEarlyRepaymentCalculator';
import { EarlyRepaymentCalculator } from 'components/EarlyRepaymentCalculator/EarlyRepaymentCalculator';
import { Offer } from 'components/SummaryCard/SummaryCard.helpers';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useLocale } from 'hooks/useLocale';
import { ConfirmOffer } from './ConfirmOffer/ConfirmOffer';
import { RejectOffer } from './RejectOffer/RejectOffer';
import { useReviewOffer } from './ReviewOffer.context';
import styles from './ReviewOffer.module.css';

export const ReviewOffer = () => {
  const { isDE, isUK } = useLocale();
  const { introducerType } = useIntroducerContext();
  const isAccountant = introducerType === 'accountant';

  const {
    hasMultipleOffers,
    selectedOfferId,
    selectedApplication,
    offerDecision,
    selectedOfferMonthlyRepayment,
  } = useReviewOffer();

  const selectedOffer =
    selectedApplication?.formal_offer_sets?.[0]?.formal_offers?.find(
      (offer) => offer.offer_id === selectedOfferId,
    );

  const renderConditionalOfferNotice = () => {
    if (
      selectedApplication?.status !== 'conditional_offer' ||
      isDE ||
      offerDecision === Offer.REJECT
    ) {
      return null;
    }

    const introducerEmail = isAccountant
      ? t`accountants@iwoca.co.uk`
      : t`brokers@iwoca.co.uk`;

    return (
      <div className={styles.conditionalOfferNotice}>
        <div className={styles.iconContainer}>
          <Icon
            className={styles.icon}
            height={24}
            width={24}
            icon="circleError"
          />
        </div>
        <p className={styles.conditionalOfferNoticeText}>
          {hasMultipleOffers ? 'These offers are' : 'This offer is'} subject to
          providing your client’s most recent VAT return and may be revoked
          based on this information. Email your client’s VAT returns to{' '}
          <a href={`mailto:${introducerEmail}`}>{introducerEmail}</a> before
          accepting this offer.
        </p>
      </div>
    );
  };

  const { available_funds, max_amount } =
    selectedApplication?.formal_offer_sets[0]?.formal_offers[0] || {};
  const availableFundsEqualOfferedAmount = available_funds === max_amount;
  const isTopUpOffer =
    available_funds && max_amount && !availableFundsEqualOfferedAmount;

  return (
    <div data-testid="review-offer" className={styles.reviewOfferContainer}>
      {renderConditionalOfferNotice()}
      {(selectedOffer || !hasMultipleOffers) &&
        offerDecision === Offer.UNDECIDED &&
        selectedApplication &&
        !isTopUpOffer && (
          <EarlyRepaymentCalculator
            formalOffer={
              selectedOffer ||
              selectedApplication?.formal_offer_sets?.[0]?.formal_offers[0]
            }
            monthlyPayments={selectedOfferMonthlyRepayment}
          />
        )}
      {hasMultipleOffers &&
        !selectedOffer &&
        offerDecision === Offer.UNDECIDED && (
          <DisabledEarlyRepaymentCalculator />
        )}
      {offerDecision === Offer.AGREE && selectedOffer && (
        <ConfirmOffer
          selectedOffer={selectedOffer}
          isRepeat={selectedApplication?.customer?.is_repeat && isUK}
        />
      )}
      {offerDecision === Offer.REJECT && <RejectOffer />}
    </div>
  );
};
